var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import TextField from '@mui/material/TextField';
import AccurityIcon from '../../icon/components/AccurityIcon';
import Autocomplete from '@mui/material/Autocomplete';
import { GlobalSearchListItem } from './GlobalSearchListItem';
import { InputAdornment } from '@mui/material';
import GlobalSearchUtils, { MORE_RESULTS_TYPE } from '../globalSearchUtils';
import FullScreenOverlay from '../../fullScreenOverlay/FullScreenOverlay';
export var GlobalSearch = function (_a) {
    var rows = _a.rows, existingObjects = _a.existingObjects, onSearch = _a.onSearch, _b = _a.noOptionsText, noOptionsText = _b === void 0 ? 'No results found' : _b, onSelect = _a.onSelect, isLoading = _a.isLoading, disabledBackgroundOverlay = _a.disabledBackgroundOverlay, _c = _a.variant, variant = _c === void 0 ? 'center' : _c, onShowMoreResults = _a.onShowMoreResults;
    // selectedValue is used to clear the input when user clicks on item in list or on search button
    var _d = useState(null), selectedValue = _d[0], setSelectedValue = _d[1];
    // last highlighted item in dropdown used to prevent looping
    var _e = useState(null), highlightedValue = _e[0], setHighlightedValue = _e[1];
    var _f = useState(''), inputValue = _f[0], setInputValue = _f[1];
    var _g = useState(false), isFocused = _g[0], setIsFocused = _g[1];
    // tells whether css animation (width: 200px -> 100%) is done. We need to show dropdown with search result after the animation is done, otherwise the dropdown is narrow
    var _h = useState(true), isAnimationFinished = _h[0], setIsAnimationFinished = _h[1];
    var wrapperRef = useRef(null);
    var styleParams = useRef({ contentRight: '0px' });
    var isFocusedRight = (variant === 'right' && isFocused);
    // when isFocus is changed, css animation fires. We need to wait till the end of animation and after that, we can show dropdown with search result
    useEffect(function () {
        if (wrapperRef.current && isFocusedRight) {
            styleParams.current = {
                contentRight: document.body.clientWidth - wrapperRef.current.getBoundingClientRect().right + "px",
            };
        }
        // animation started
        setIsAnimationFinished(false);
        // set the value to true after exact timeout (same as in css transition property for animating width of .globalSearch)
        var timeout = setTimeout(function () { return setIsAnimationFinished(true); }, 350);
        // reset counting when another change of isFocused occurred
        return function () { return clearTimeout(timeout); };
    }, [isFocused]);
    var handleInputChange = function (value) {
        onSearch(value);
        setInputValue(value);
    };
    var handleItemSelect = function (value) {
        if (value) {
            if (value.objectType === MORE_RESULTS_TYPE) {
                onShowMoreResults(inputValue);
            }
            else {
                onSelect(value);
            }
            setSelectedValue(value);
            setInputValue('');
        }
        // resetting the input component to empty state has to be done in next render cycle in order
        // to allow Autocomplete component run a change detection and re-render component
        setTimeout(function () {
            setSelectedValue(null);
            GlobalSearchUtils.looseFocus();
        }, 1);
    };
    // do NOT filter items on frontend and rely only on backend filtering => display all items returned from backed
    var filterOptions = function (options) { return options; };
    var isDropdownOpen = isFocused && isAnimationFinished;
    // defined onKeyDown handler to prevent propagating KeyDown/Up events to Autocomplete component
    // the purpose of blocking this event is to prevent looping in list of options
    var onKeyDownHandler = function (event) {
        if (!!highlightedValue) {
            if ((event.key === 'ArrowDown' && rows.indexOf(highlightedValue) === rows.length - 1) ||
                (event.key === 'ArrowUp' && rows.indexOf(highlightedValue) === 0)) {
                event.stopPropagation();
            }
        }
    };
    if (inputValue.length === 0) {
        noOptionsText = "Type to start searching";
    }
    else if (isLoading) {
        noOptionsText = "Searching";
    }
    if (existingObjects > 0) {
        rows = __spreadArray(__spreadArray([], rows, true), [GlobalSearchUtils.createMoreResultListItem(existingObjects)], false);
    }
    return (React.createElement("div", { ref: wrapperRef, className: classNames('globalSearch__wrapper', {
            'globalSearch__wrapper--center': variant === 'center',
            'globalSearch__wrapper--right': variant === 'right',
        }) },
        React.createElement("div", { style: (variant === 'right') ? { right: styleParams.current.contentRight } : {}, className: classNames({
                'globalSearch__content': variant === 'right',
                'globalSearch__contentRight--focused': isFocusedRight,
            }) },
            React.createElement("div", { className: classNames('globalSearch', {
                    'globalSearch--hasValue': inputValue.length > 0,
                    'globalSearch--focused': isFocused,
                    'globalSearch--disabledBackgroundOverlay': disabledBackgroundOverlay,
                }), onKeyDownCapture: onKeyDownHandler },
                isFocused && !disabledBackgroundOverlay && React.createElement(FullScreenOverlay, null),
                React.createElement(Autocomplete, { className: classNames('globalSearch__autocomplete', {
                        'globalSearch__autocomplete--hasValue': inputValue.length > 0,
                        'globalSearch__autocomplete--focused': inputValue.length > 0,
                    }), options: rows, autoHighlight: true, getOptionLabel: function (option) { return option.name; }, filterOptions: filterOptions, renderOption: function (props, option) {
                        return React.createElement("li", __assign({}, props, { key: option.objectType + "-" + option.id }),
                            React.createElement(GlobalSearchListItem, __assign({}, option, { searchTerm: inputValue })));
                    }, renderInput: function (params) { return React.createElement(TextField, __assign({}, params, { placeholder: 'Search', variant: 'standard', size: 'medium', color: "error", InputProps: __assign(__assign({}, params.InputProps), { startAdornment: (React.createElement(InputAdornment, { position: "start", disablePointerEvents: true },
                                React.createElement(AccurityIcon, { iconName: "mui:Search", focusable: false }))) }), onBlur: function () { return setIsFocused(false); }, onFocus: function () { return setIsFocused(true); } })); }, onInputChange: function (event, value) { return handleInputChange(value); }, onChange: function (event, value) { return handleItemSelect(value); }, onHighlightChange: function (event, value) { return setHighlightedValue(value); }, value: selectedValue, open: isDropdownOpen, disablePortal: true, noOptionsText: noOptionsText, clearOnBlur: false, forcePopupIcon: false })))));
};
export default GlobalSearch;
//# sourceMappingURL=GlobalSearch.js.map